import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Flex,
  Heading,
  Text,
  Button,
  VStack,
  HStack,
  Badge,
  Divider,
  Spinner,
  Switch,
  FormControl,
  FormLabel,
  RadioGroup,
  Radio,
  Stack,
  Select,
  useToast,
  useDisclosure,
} from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { getLocations } from '../../organization/api/organizationApi';
import {
  useAlertConfig,
  useSaveAlertConfig,
  useToggleAlertEnabled,
  useAlertStatus,
  useAlertHistory,
} from '../hooks/useAlerts';
import { RegionPickerModal } from '../components/RegionPickerModal';
import { ClipPickerModal } from '../components/ClipPickerModal';
import type { DuringAlarmBehavior } from '../types';

const BEHAVIORS: DuringAlarmBehavior[] = ['continue', 'repeat', 'silence'];

export function AlertsPage() {
  const { t } = useTranslation();
  const toast = useToast();

  const { data: allLocations } = useQuery({ queryKey: ['organization', 'locations'], queryFn: getLocations });
  const uaLocations = useMemo(
    () => (allLocations ?? []).filter((l) => l.address?.country === 'UA'),
    [allLocations],
  );
  const [locationId, setLocationId] = useState('');
  useEffect(() => {
    if (uaLocations.length && !locationId) setLocationId(uaLocations[0].id);
  }, [uaLocations, locationId]);

  const { data: config, isLoading } = useAlertConfig(locationId || undefined);
  const save = useSaveAlertConfig(locationId);
  const toggle = useToggleAlertEnabled(locationId);
  const { data: status } = useAlertStatus(locationId || undefined, !!config?.enabled);
  const history = useAlertHistory(locationId || undefined);

  const regionPicker = useDisclosure();
  const startPicker = useDisclosure();
  const endPicker = useDisclosure();

  const [regionId, setRegionId] = useState<string | null>(null);
  const [regionName, setRegionName] = useState<string | null>(null);
  const [startClip, setStartClip] = useState<{ id: string; name: string } | null>(null);
  const [endClip, setEndClip] = useState<{ id: string; name: string } | null>(null);
  const [behavior, setBehavior] = useState<DuringAlarmBehavior>('continue');
  const [syncedAt, setSyncedAt] = useState('');

  useEffect(() => {
    if (config && config.updatedAt !== syncedAt) {
      setRegionId(config.regionId);
      setRegionName(config.regionName);
      setStartClip(config.alarmStartContentId ? { id: config.alarmStartContentId, name: config.alarmStartContentName ?? '' } : null);
      setEndClip(config.alarmEndContentId ? { id: config.alarmEndContentId, name: config.alarmEndContentName ?? '' } : null);
      setBehavior(config.duringAlarmBehavior);
      setSyncedAt(config.updatedAt);
    }
  }, [config, syncedAt]);

  const canSave = !!regionId && !!startClip;

  const handleSave = () => {
    save.mutate(
      {
        regionId,
        alarmStartContentId: startClip?.id ?? null,
        alarmEndContentId: endClip?.id ?? null,
        duringAlarmBehavior: behavior,
      },
      {
        onSuccess: () => toast({ status: 'success', title: t('alerts.saved'), duration: 2000 }),
        onError: () => toast({ status: 'error', title: t('common.error', 'Something went wrong') }),
      },
    );
  };

  const handleToggle = () => {
    toggle.mutate(!config?.enabled, {
      onError: () => toast({ status: 'error', title: t('common.error', 'Something went wrong') }),
    });
  };

  if (allLocations && uaLocations.length === 0) {
    return (
      <Box>
        <Heading size="lg" color="gray.800" mb={2}>
          {t('alerts.title')}
        </Heading>
        <Text color="gray.500">{t('alerts.uaOnly')}</Text>
      </Box>
    );
  }

  const historyItems = history.data?.pages.flatMap((p) => p.items) ?? [];

  return (
    <Box maxW="720px">
      <Heading size="lg" color="gray.800" mb={4}>
        {t('alerts.title')}
      </Heading>

      <Select maxW="320px" mb={5} value={locationId} onChange={(e) => setLocationId(e.target.value)} bg="white">
        {uaLocations.map((l) => (
          <option key={l.id} value={l.id}>
            {l.name}
          </option>
        ))}
      </Select>

      {status?.isAlertActive && (
        <Box bg="red.50" borderWidth="1px" borderColor="red.200" borderRadius="md" px={4} py={3} mb={4}>
          <HStack>
            <Text color="red.600" fontWeight="700">
              {t('alerts.activeNow')}
            </Text>
            {status.isSimulated && <Badge colorScheme="gray">{t('alerts.simulated')}</Badge>}
          </HStack>
          <Text color="red.500" fontSize="sm">
            {status.regionName ?? ''}
            {status.startedAt ? ` · ${new Date(status.startedAt).toLocaleTimeString()}` : ''}
          </Text>
        </Box>
      )}

      {isLoading ? (
        <Flex justify="center" py={16}>
          <Spinner size="xl" color="brand.500" thickness="3px" />
        </Flex>
      ) : (
        <>
          <FormControl display="flex" alignItems="center" mb={5}>
            <FormLabel htmlFor="alert-enabled" mb={0} mr={3}>
              {t('alerts.monitoring')}
            </FormLabel>
            <Switch
              id="alert-enabled"
              isChecked={!!config?.enabled}
              onChange={handleToggle}
              isDisabled={toggle.isPending || !config?.regionId}
              colorScheme="red"
            />
          </FormControl>

          <VStack align="stretch" spacing={4} mb={6}>
            <Box>
              <Text fontSize="sm" fontWeight="600" mb={1}>
                {t('alerts.region')}
              </Text>
              <Button variant="outline" w="100%" justifyContent="flex-start" onClick={regionPicker.onOpen}>
                {regionName || t('alerts.selectRegion')}
              </Button>
            </Box>

            <Box>
              <Text fontSize="sm" fontWeight="600" mb={1}>
                {t('alerts.startAudio')}
              </Text>
              <Button variant="outline" w="100%" justifyContent="flex-start" onClick={startPicker.onOpen}>
                {startClip?.name || t('alerts.selectClip')}
              </Button>
            </Box>

            <Box>
              <Text fontSize="sm" fontWeight="600" mb={1}>
                {t('alerts.endAudio')}
              </Text>
              <HStack>
                <Button variant="outline" flex={1} justifyContent="flex-start" onClick={endPicker.onOpen}>
                  {endClip?.name || t('alerts.selectClipOptional')}
                </Button>
                {endClip && (
                  <Button variant="ghost" size="sm" onClick={() => setEndClip(null)}>
                    {t('common.delete')}
                  </Button>
                )}
              </HStack>
            </Box>

            <Box>
              <Text fontSize="sm" fontWeight="600" mb={2}>
                {t('alerts.duringAlarm')}
              </Text>
              <RadioGroup value={behavior} onChange={(v) => setBehavior(v as DuringAlarmBehavior)}>
                <Stack spacing={2}>
                  {BEHAVIORS.map((b) => (
                    <Radio key={b} value={b} colorScheme="brand">
                      {t(`alerts.behaviors.${b}`)}
                    </Radio>
                  ))}
                </Stack>
              </RadioGroup>
            </Box>

            <Button colorScheme="brand" onClick={handleSave} isLoading={save.isPending} isDisabled={!canSave} alignSelf="flex-start">
              {t('common.save')}
            </Button>
          </VStack>
        </>
      )}

      <Divider my={6} />

      <Heading size="md" color="gray.700" mb={3}>
        {t('alerts.history')}
      </Heading>
      {history.isLoading ? (
        <Flex justify="center" py={8}>
          <Spinner color="brand.500" />
        </Flex>
      ) : historyItems.length === 0 ? (
        <Text color="gray.400" py={8} textAlign="center">
          {t('alerts.noHistory')}
        </Text>
      ) : (
        <>
          {historyItems.map((h) => (
            <HStack key={h.id} py={2.5} px={2} spacing={4} borderBottomWidth="1px" borderColor="gray.100">
              <Box flex={1} minW={0}>
                <HStack spacing={2}>
                  <Text fontSize="sm" color="gray.800">
                    {new Date(h.startedAt).toLocaleString()}
                  </Text>
                  {h.isSimulated && <Badge colorScheme="gray" fontSize="0.6rem">{t('alerts.simulated')}</Badge>}
                </HStack>
                <Text fontSize="xs" color="gray.400">
                  {t(`alerts.behaviors.${h.playbackAction}`, h.playbackAction)}
                </Text>
              </Box>
              {h.durationSeconds != null && (
                <Text fontSize="sm" color="gray.400">
                  {Math.round(h.durationSeconds / 60)}m
                </Text>
              )}
            </HStack>
          ))}
          {history.hasNextPage && (
            <Flex justify="center" mt={4}>
              <Button variant="outline" onClick={() => history.fetchNextPage()} isLoading={history.isFetchingNextPage}>
                {t('playlists.loadMore')}
              </Button>
            </Flex>
          )}
        </>
      )}

      <RegionPickerModal
        isOpen={regionPicker.isOpen}
        onClose={regionPicker.onClose}
        onSelect={(r) => {
          setRegionId(r.regionId);
          setRegionName(r.regionName);
        }}
      />
      <ClipPickerModal
        isOpen={startPicker.isOpen}
        onClose={startPicker.onClose}
        title={t('alerts.startAudio')}
        onSelect={(c) => setStartClip({ id: c.id, name: c.name })}
      />
      <ClipPickerModal
        isOpen={endPicker.isOpen}
        onClose={endPicker.onClose}
        title={t('alerts.endAudio')}
        onSelect={(c) => setEndClip({ id: c.id, name: c.name })}
      />
    </Box>
  );
}

export default AlertsPage;
